/**
 * MBA Lab — pure state model for the Peer Evaluation Drafter. No DOM,
 * storage, or analytics dependencies - peer-evaluation-drafter.js owns all of
 * that.
 *
 * Session-only, like listening-model.js and for a stronger version of the same
 * reason. Every field here is a candid judgment about a named classmate the
 * user has to keep working with, and the calibration checkboxes are worse
 * still: they are attestations *about one specific piece of text* ("would I say
 * this to them in these words?"). Strip the text and keep the attestation and
 * you have stored a meaningless boolean about a draft that no longer exists -
 * so there is no opt-in "remember on this device" tier here at all, and
 * buildPlainText() exists so the user can move their own draft to wherever
 * they actually want it.
 *
 * Two shape decisions worth stating up front, because both are load-bearing:
 *
 *  1. There is no free-form summary field, on purpose. That box is where
 *     "great teammate" boilerplate lives, which is the exact failure this tool
 *     exists to prevent - see Content/Blog/peer-evaluations-mba-teams.md.
 *  2. `calibration` is never exported. It is a private pre-flight check for the
 *     person writing, not part of the feedback the teammate or instructor sees,
 *     and buildPlainText() has a test asserting it stays out.
 */

import {
  STRENGTH_FIELDS,
  GAP_FIELDS,
  RAISED_OPTIONS,
  RAISED_ACKNOWLEDGMENT_SEED,
  CALIBRATION_CHECKS,
  EXPORT_HEADINGS,
  EXPORT_TITLE,
  EXPORT_EMPTY_NOTE,
} from './peer-eval-prompts.js';

export const SCHEMA_NAME = 'mbalab.peer-evaluation-drafter';
export const SCHEMA_VERSION = 1;

/** One tab per teammate. Eight covers every MBA study team this tool has any
 * business supporting - the same ceiling the team charter builder puts on its
 * member list. */
export const MAX_EVALUATIONS = 8;

/** sectionsStartedFor() is out of this many - see its own doc comment. */
export const SECTION_COUNT = 5;

export const LIMITS = Object.freeze({
  teammateLabel: 60,
  fieldLength: 800,
  // Worst case the caps above permit: 8 evaluations x 6 text fields x 800 chars
  // + labels + structural JSON, rounded well up. sessionStorage is per-tab and
  // short-lived, so there is no reason to run this close to any quota.
  maxSerializedBytes: 128 * 1024,
});

export const RAISED_VALUES = Object.freeze(RAISED_OPTIONS.map((o) => o.value));

const CALIBRATION_KEYS = Object.freeze(CALIBRATION_CHECKS.map((c) => c.key));

/** The six free-text fields, in the order buildPlainText() prints them.
 * `teammateLabel` is deliberately not in here: it is the block heading, not a
 * body field, and it is clamped to a much shorter length. */
export const TEXT_FIELDS = Object.freeze([
  ...STRENGTH_FIELDS.map((f) => f.key),
  ...GAP_FIELDS.map((f) => f.key),
  'raisedNote',
]);

const TEXT_FIELD_SET = new Set(TEXT_FIELDS);

/** slice, never trim - these are bound to live inputs, and trimming on every
 * keystroke eats the space the user just typed between two words. Matches
 * listening-model.js's clampText for the same reason. */
function clampText(value, maxLen) {
  return typeof value === 'string' ? value.slice(0, maxLen) : '';
}

function nonBlank(value) {
  return typeof value === 'string' && value.trim() !== '';
}

function makeEvaluationId() {
  return 'peer_' + Math.random().toString(36).slice(2, 10);
}

function emptyCalibration() {
  const out = {};
  for (const key of CALIBRATION_KEYS) out[key] = false;
  return out;
}

export function createEvaluation(id = makeEvaluationId()) {
  const evaluation = {
    id,
    teammateLabel: '',
    raised: '',
    // True only while `raisedNote` still holds the exact sentence this tool
    // put there. Cleared the instant the user changes it, and never set again
    // unless the field goes back to blank and they re-answer "no".
    raisedNoteSeeded: false,
    calibration: emptyCalibration(),
  };
  for (const key of TEXT_FIELDS) evaluation[key] = '';
  return evaluation;
}

export function createInitialState() {
  return {
    schema: SCHEMA_NAME,
    version: SCHEMA_VERSION,
    savedAt: null,
    evaluations: [],
    // -1 means "nothing selected", which is the honest state with an empty
    // list. getActiveEvaluation() returns null for it rather than index 0 of
    // an empty array.
    activeIndex: -1,
  };
}

export function getActiveEvaluation(state) {
  return state.evaluations[state.activeIndex] ?? null;
}

/** Appends a blank evaluation and selects it. A no-op at MAX_EVALUATIONS -
 * returns the same state object so callers can compare by identity. */
export function addEvaluation(state, id) {
  if (state.evaluations.length >= MAX_EVALUATIONS) return state;
  const evaluations = [...state.evaluations, createEvaluation(id)];
  return { ...state, evaluations, activeIndex: evaluations.length - 1 };
}

/**
 * Removes one evaluation and keeps activeIndex pointing at something sensible:
 * removing a tab to the LEFT of the active one shifts it down by one (so the
 * same evaluation stays selected), removing the active one selects whatever
 * slid into its place (or the new last tab, if it was the last), and removing
 * the only one leaves nothing selected.
 */
export function removeEvaluation(state, index) {
  if (!Number.isInteger(index) || index < 0 || index >= state.evaluations.length) return state;
  const evaluations = state.evaluations.filter((_, i) => i !== index);

  let activeIndex;
  if (!evaluations.length) activeIndex = -1;
  else if (index < state.activeIndex) activeIndex = state.activeIndex - 1;
  else if (index === state.activeIndex) activeIndex = Math.min(index, evaluations.length - 1);
  else activeIndex = state.activeIndex;

  return { ...state, evaluations, activeIndex };
}

export function selectEvaluation(state, index) {
  if (!Number.isInteger(index) || index < 0 || index >= state.evaluations.length) return state;
  return { ...state, activeIndex: index };
}

function replaceEvaluation(state, index, updater) {
  if (!Number.isInteger(index) || index < 0 || index >= state.evaluations.length) return state;
  const evaluations = state.evaluations.map((e, i) => (i === index ? updater(e) : e));
  return { ...state, evaluations };
}

export function setTeammateLabel(state, index, value) {
  return replaceEvaluation(state, index, (e) => ({
    ...e,
    teammateLabel: clampText(value, LIMITS.teammateLabel),
  }));
}

/** Setter for any of the six body fields. Writing to `raisedNote` clears the
 * seeded flag unless the value happens to be exactly the seed sentence, which
 * is what makes an untouched seed distinguishable from a user's own words. */
export function setEvaluationField(state, index, field, value) {
  if (!TEXT_FIELD_SET.has(field)) return state;
  return replaceEvaluation(state, index, (e) => {
    const next = { ...e, [field]: clampText(value, LIMITS.fieldLength) };
    if (field === 'raisedNote' && next.raisedNote !== RAISED_ACKNOWLEDGMENT_SEED) {
      next.raisedNoteSeeded = false;
    }
    return next;
  });
}

/**
 * The proportionality answer. Rejects anything outside the enum outright
 * (returns state unchanged) rather than storing an unknown string that every
 * downstream comparison would then silently treat as "not answered".
 *
 * Answering "no" seeds the article's own acknowledgment sentence into
 * `raisedNote`, but only into a blank field - it never overwrites something the
 * user wrote. Answering anything else clears a still-untouched seed, so a
 * placeholder acknowledgment can't linger under a "yes, I raised it" answer.
 */
export function setRaised(state, index, value) {
  if (!RAISED_VALUES.includes(value)) return state;
  return replaceEvaluation(state, index, (e) => {
    const next = { ...e, raised: value };
    if (value === 'no') {
      if (!nonBlank(next.raisedNote)) {
        next.raisedNote = RAISED_ACKNOWLEDGMENT_SEED;
        next.raisedNoteSeeded = true;
      }
    } else if (isUntouchedSeed(next)) {
      next.raisedNote = '';
      next.raisedNoteSeeded = false;
    }
    return next;
  });
}

export function setCalibration(state, index, key, checked) {
  if (!CALIBRATION_KEYS.includes(key)) return state;
  return replaceEvaluation(state, index, (e) => ({
    ...e,
    calibration: { ...e.calibration, [key]: !!checked },
  }));
}

/** True while `raisedNote` is still the exact sentence this tool seeded and
 * nothing has been typed over it. Both halves are required: the flag alone
 * would survive a hand-edited save that changed the text, and the text
 * comparison alone would misread a user who typed that sentence themselves. */
export function isUntouchedSeed(evaluation) {
  return !!evaluation
    && evaluation.raisedNoteSeeded === true
    && evaluation.raisedNote === RAISED_ACKNOWLEDGMENT_SEED;
}

/**
 * Whether the article's proportionality step is genuinely done.
 *
 * "Yes, I raised it" and "there is nothing hard here" settle it on their own.
 * "No" does not: the article's instruction for that case is not merely to
 * admit it internally but to *write it down* - "you can still write it, but
 * write it acknowledging that." A seed sentence this tool typed for you is not
 * you acknowledging anything, so it stays unsettled until the user makes the
 * sentence theirs.
 */
export function isProportionalitySettled(evaluation) {
  if (!evaluation) return false;
  if (evaluation.raised === 'yes' || evaluation.raised === 'nothing-hard') return true;
  if (evaluation.raised !== 'no') return false;
  return nonBlank(evaluation.raisedNote) && !isUntouchedSeed(evaluation);
}

export function hasStrengthContent(evaluation) {
  return !!evaluation && STRENGTH_FIELDS.some((f) => nonBlank(evaluation[f.key]));
}

export function hasGapContent(evaluation) {
  return !!evaluation && GAP_FIELDS.some((f) => nonBlank(evaluation[f.key]));
}

export function calibrationCount(evaluation) {
  if (!evaluation || !evaluation.calibration) return 0;
  return CALIBRATION_KEYS.filter((k) => evaluation.calibration[k] === true).length;
}

/**
 * How much of one evaluation has been started, out of SECTION_COUNT (5). The
 * five, and why these five:
 *
 *   1. label        - who this draft is about, so the tabs mean something.
 *   2. strength     - the article's "say what actually worked, specifically".
 *   3. gap          - the article's "write about behaviour, not character".
 *   4. proportionality - the article's "was this raised during the project?",
 *                     settled per isProportionalitySettled() above.
 *   5. calibration  - at least one self-check ticked.
 *
 * Deliberately "started", not "finished": this counts sections the user has
 * engaged with, and is what the progress reading and the one-shot tool_result
 * threshold both read. Nothing here is a score of the feedback's quality - the
 * per-field checks are the only quality signal this tool shows, and they are
 * per-field on purpose.
 */
export function sectionsStartedFor(evaluation) {
  if (!evaluation) return 0;
  let started = 0;
  if (nonBlank(evaluation.teammateLabel)) started += 1;
  if (hasStrengthContent(evaluation)) started += 1;
  if (hasGapContent(evaluation)) started += 1;
  if (isProportionalitySettled(evaluation)) started += 1;
  if (calibrationCount(evaluation) > 0) started += 1;
  return started;
}

export function maxSectionsStarted(state) {
  return state.evaluations.reduce((max, e) => Math.max(max, sectionsStartedFor(e)), 0);
}

/**
 * Ready to paste into a real course form. Calibration is deliberately NOT
 * required: turning five honesty attestations into a gate that unlocks a button
 * converts them from a moment of reflection into five clicks.
 *
 * The gap clause is the article's two hard rules taken together - if there is
 * any criticism at all, it needs both the observable thing and the cost,
 * because "feedback without cost attached is easy to file away as someone's
 * preference." An evaluation with nothing critical in it is perfectly valid and
 * is not held to that.
 */
export function isEvaluationReady(evaluation) {
  if (!evaluation) return false;
  if (!nonBlank(evaluation.teammateLabel)) return false;
  if (!nonBlank(evaluation.strengthObservation)) return false;
  if (!isProportionalitySettled(evaluation)) return false;
  if (hasGapContent(evaluation) && !(nonBlank(evaluation.gapObservation) && nonBlank(evaluation.gapCost))) {
    return false;
  }
  return true;
}

/** Anything worth exporting for this one evaluation. An untouched seed does
 * not count as the user having written something, but the "no" answer that
 * produced it does. */
export function evaluationHasContent(evaluation) {
  if (!evaluation) return false;
  if (nonBlank(evaluation.teammateLabel)) return true;
  if (evaluation.raised !== '') return true;
  for (const key of TEXT_FIELDS) {
    if (key === 'raisedNote') {
      if (nonBlank(evaluation.raisedNote) && !isUntouchedSeed(evaluation)) return true;
      continue;
    }
    if (nonBlank(evaluation[key])) return true;
  }
  return false;
}

export function hasAnyContent(state) {
  return state.evaluations.some(evaluationHasContent);
}

function raisedLabelFor(value) {
  return RAISED_OPTIONS.find((o) => o.value === value)?.label ?? '';
}

/**
 * PLAIN TEXT, not markdown - unlike the charter and the journal, the
 * destination for this is a textarea on a course evaluation form, where
 * markdown syntax would be pasted in literally and read as noise. Nothing here
 * is ever re-rendered through a markdown pipeline, so there is nothing to
 * escape against either.
 *
 * No "built with MBA Lab" credit line, deliberately, unlike every other export
 * on this site: this text is pasted verbatim into a graded academic form, and a
 * tool's promotional footer has no business showing up in someone's peer
 * review.
 *
 * Never prints the calibration checkboxes. They are the writer's private
 * pre-flight check, not feedback about the teammate.
 */
export function buildPlainText(state) {
  const lines = [EXPORT_TITLE, ''];
  const withContent = (state && Array.isArray(state.evaluations) ? state.evaluations : [])
    .filter(evaluationHasContent);

  if (!withContent.length) {
    lines.push(EXPORT_EMPTY_NOTE);
    return lines.join('\n');
  }

  withContent.forEach((evaluation, i) => {
    if (i > 0) lines.push('---', '');
    lines.push(nonBlank(evaluation.teammateLabel) ? evaluation.teammateLabel.trim() : `Teammate ${i + 1}`, '');

    if (hasStrengthContent(evaluation)) {
      lines.push(EXPORT_HEADINGS.strength);
      for (const field of STRENGTH_FIELDS) {
        if (nonBlank(evaluation[field.key])) lines.push(evaluation[field.key].trim());
      }
      lines.push('');
    }

    if (hasGapContent(evaluation)) {
      lines.push(EXPORT_HEADINGS.gap);
      if (nonBlank(evaluation.gapObservation)) lines.push(evaluation.gapObservation.trim());
      if (nonBlank(evaluation.gapCost)) lines.push(evaluation.gapCost.trim());
      if (nonBlank(evaluation.gapAlternative)) {
        lines.push(`${EXPORT_HEADINGS.alternative} ${evaluation.gapAlternative.trim()}`);
      }
      lines.push('');
    }

    const raisedLabel = raisedLabelFor(evaluation.raised);
    if (raisedLabel) {
      lines.push(`${EXPORT_HEADINGS.raised} ${raisedLabel}`);
      // Two separate guards, both required. !isUntouchedSeed keeps the tool's own seed
      // sentence from printing as if the user wrote it. raised === 'no' keeps a note the
      // user genuinely DID write from surviving under the wrong heading if they later
      // switch to 'yes'/'nothing-hard' - setRaised() only clears an untouched seed on that
      // switch, by design, so it never silently deletes real typed text - but this field is
      // only ever shown in the UI while raised === 'no' (see raisedNoteWrap.hidden), and a
      // note about not having raised something is meaningless, out-of-context prose once the
      // answer above it says the opposite.
      if (evaluation.raised === 'no' && nonBlank(evaluation.raisedNote) && !isUntouchedSeed(evaluation)) {
        lines.push(evaluation.raisedNote.trim());
      }
      lines.push('');
    }
  });

  // One trailing blank line at most, whatever the last block happened to be.
  while (lines.length && lines[lines.length - 1] === '') lines.pop();
  return lines.join('\n');
}

function sanitizeEvaluation(raw, index) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const evaluation = createEvaluation(typeof r.id === 'string' && r.id ? r.id : `peer_restored_${index}`);
  evaluation.teammateLabel = clampText(r.teammateLabel, LIMITS.teammateLabel);
  for (const key of TEXT_FIELDS) evaluation[key] = clampText(r[key], LIMITS.fieldLength);
  evaluation.raised = RAISED_VALUES.includes(r.raised) ? r.raised : '';
  // The flag only survives a round trip while the text it describes still
  // matches - a hand-edited save that kept the flag but changed the sentence
  // must not be able to make a user's own words read as an untouched seed.
  evaluation.raisedNoteSeeded = r.raisedNoteSeeded === true
    && evaluation.raisedNote === RAISED_ACKNOWLEDGMENT_SEED;
  const rawCalibration = r.calibration && typeof r.calibration === 'object' ? r.calibration : {};
  for (const key of CALIBRATION_KEYS) evaluation.calibration[key] = rawCalibration[key] === true;
  return evaluation;
}

export function sanitizeState(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const evaluations = (Array.isArray(r.evaluations) ? r.evaluations : [])
    .slice(0, MAX_EVALUATIONS)
    .map(sanitizeEvaluation);

  let activeIndex = Number.isInteger(r.activeIndex) ? r.activeIndex : -1;
  if (!evaluations.length) activeIndex = -1;
  else if (activeIndex < 0 || activeIndex >= evaluations.length) activeIndex = 0;

  return {
    schema: SCHEMA_NAME,
    version: SCHEMA_VERSION,
    savedAt: typeof r.savedAt === 'number' ? r.savedAt : null,
    evaluations,
    activeIndex,
  };
}

const MIGRATIONS = {
  // No prior schema version has ever shipped - same note as charter-model.js
  // and listening-model.js. Proves the mechanism works before it's needed.
  0: (state) => ({ ...state, version: 1 }),
};

function migrate(rawState) {
  let state = rawState;
  let migrated = false;
  while (typeof state.version === 'number' && state.version < SCHEMA_VERSION && MIGRATIONS[state.version]) {
    state = MIGRATIONS[state.version](state);
    migrated = true;
  }
  return { state, migrated };
}

/** @returns {{status: 'ok'|'migrated'|'unsupported-newer'|'malformed', state: object|null, raw: string|null}} */
export function parseSavedState(raw) {
  let parsed;
  let rawString;

  if (typeof raw === 'string') {
    rawString = raw;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return { status: 'malformed', state: null, raw: rawString };
    }
  } else {
    parsed = raw;
    try {
      rawString = raw != null ? JSON.stringify(raw) : null;
    } catch {
      rawString = null;
    }
  }

  if (
    !parsed
    || typeof parsed !== 'object'
    || parsed.schema !== SCHEMA_NAME
    || !Number.isInteger(parsed.version)
    || parsed.version < 0
  ) {
    // Number.isInteger(NaN) is false, so a corrupted NaN version is rejected
    // here rather than falling through the migrate loop as already-current.
    return { status: 'malformed', state: null, raw: rawString };
  }
  if (parsed.version > SCHEMA_VERSION) {
    return { status: 'unsupported-newer', state: null, raw: rawString };
  }

  const { state: migratedState, migrated } = migrate(parsed);
  return { status: migrated ? 'migrated' : 'ok', state: sanitizeState(migratedState), raw: rawString };
}

export function serializeForSave(state) {
  const json = JSON.stringify(state);
  if (typeof TextEncoder !== 'undefined' && new TextEncoder().encode(json).length > LIMITS.maxSerializedBytes) {
    return null;
  }
  return json;
}
